/**
 * Results Table - Flat table view for evaluation results
 */

import {
  getRepairStatus,
  getStageStatus,
  getStatusIndicator,
  type StageStatus,
} from './StageStatusCell';

interface EvalResult {
  id: string;
  prompt_id: string;
  model_config_id: string;
  is_valid_json: boolean;
  error?: string;
  execution_time_ms?: number;
  created_at: string;
  prompt?: any;
  model_config?: any;
  eval_run?: any;
  evaluation_ok?: boolean;
  formula_errors?: boolean;
  api_call_error?: string | null;
  json_parse_error?: string | null;
  schema_validation_error?: any[] | null;
  repair_error?: string | null;
  formula_evaluation_error?: string | null;
}

interface ResultsTableProps {
  results: EvalResult[];
  onResultClick: (resultId: string) => void;
}

function formatTimestamp(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
}

function formatExecutionTime(ms: number | undefined): string {
  if (!ms) return '—';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${seconds}s`;
}

function getStatusColor(status: StageStatus): string {
  switch (status) {
    case '✓':
      return '#2E7D4A';
    case '✗':
      return '#C41E3A';
    case '⚠':
      return '#B8860B';
    default:
      return '#A0A0A0';
  }
}

/** Get display name for a prompt, falling back to truncated text if name is missing */
function getPromptDisplayName(prompt: any): string {
  if (prompt?.name) return prompt.name;
  if (prompt?.text) {
    const truncated = prompt.text.slice(0, 40);
    return truncated.length < prompt.text.length
      ? `${truncated}...`
      : truncated;
  }
  return 'Unknown';
}

export function ResultsTable({ results, onResultClick }: ResultsTableProps) {
  return (
    <div
      className="overflow-x-auto bg-white rounded-xl"
      style={{ border: '1px solid #E8E0D8' }}
    >
      <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr
            style={{ background: '#F5F0EB', borderBottom: '2px solid #E8E0D8' }}
          >
            <th
              className="px-3 py-3 text-left font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Status
            </th>
            <th
              className="px-3 py-3 text-left font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Prompt
            </th>
            <th
              className="px-3 py-3 text-left font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Config
            </th>
            <th
              className="px-3 py-3 text-left font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Model
            </th>
            <th
              className="px-3 py-3 text-center font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Time
            </th>
            <th
              className="px-3 py-3 text-center font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              API
            </th>
            <th
              className="px-3 py-3 text-center font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Parse
            </th>
            <th
              className="px-3 py-3 text-center font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Schema
            </th>
            <th
              className="px-3 py-3 text-center font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Repair
            </th>
            <th
              className="px-3 py-3 text-center font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Result
            </th>
            <th
              className="px-3 py-3 text-left font-semibold"
              style={{ color: '#1A1A1A' }}
            >
              Timestamp
            </th>
          </tr>
        </thead>
        <tbody>
          {results.map(result => {
            const status = getStatusIndicator(result);
            const apiStatus = getStageStatus(result, 'api_call');
            const parseStatus = getStageStatus(result, 'json_parse');
            const schemaStatus = getStageStatus(result, 'schema_validation');
            const repairStatus = getRepairStatus(result);

            return (
              <tr
                key={result.id}
                onClick={() => onResultClick(result.id)}
                className="cursor-pointer transition-colors duration-150"
                style={{ borderBottom: '1px solid #E8E0D8' }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(139, 35, 50, 0.04)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <td
                  className="px-3 py-3 text-center text-xl"
                  title={status.label}
                >
                  {status.icon}
                </td>
                <td
                  className="px-3 py-3 font-medium max-w-xs truncate"
                  style={{ color: '#1A1A1A' }}
                  title={result.prompt?.text}
                >
                  {getPromptDisplayName(result.prompt)}
                </td>
                <td className="px-3 py-3" style={{ color: '#6B6B6B' }}>
                  {result.model_config?.name || 'Unknown'}
                </td>
                <td className="px-3 py-3 text-xs" style={{ color: '#6B6B6B' }}>
                  {result.model_config?.provider}/
                  {result.model_config?.api_model || '—'}
                </td>
                <td
                  className="px-3 py-3 text-center"
                  style={{ color: '#A0A0A0' }}
                >
                  {formatExecutionTime(result.execution_time_ms)}
                </td>
                <td
                  className="px-3 py-3 text-center"
                  style={{ color: getStatusColor(apiStatus) }}
                >
                  {apiStatus}
                </td>
                <td
                  className="px-3 py-3 text-center"
                  style={{ color: getStatusColor(parseStatus) }}
                >
                  {parseStatus}
                </td>
                <td
                  className="px-3 py-3 text-center"
                  style={{ color: getStatusColor(schemaStatus) }}
                >
                  {schemaStatus}
                </td>
                <td
                  className="px-3 py-3 text-center"
                  style={{ color: getStatusColor(repairStatus) }}
                >
                  {repairStatus}
                </td>
                <td
                  className="px-3 py-3 text-center"
                  style={{
                    color:
                      result.evaluation_ok === true
                        ? '#2E7D4A'
                        : result.evaluation_ok === false
                          ? '#C41E3A'
                          : '#A0A0A0',
                  }}
                >
                  {result.evaluation_ok === true
                    ? '✓'
                    : result.evaluation_ok === false
                      ? '✗'
                      : '—'}
                </td>
                <td
                  className="px-3 py-3 text-xs whitespace-nowrap"
                  style={{ color: '#A0A0A0' }}
                >
                  {formatTimestamp(result.created_at)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
